/**
 * V52 — Country-wise curriculum + board + subject map for the Foreign / NRI
 * tuition mode. India Home Tuition + Pan-India online forms continue to use
 * the master-data backed `board` list — this file is consulted ONLY when
 * `mode === "Live Online (Foreign / NRI)"`.
 *
 * Shape:
 *   key   = ISO-3166 alpha-2 country code (uppercased)
 *   value = {
 *     name, flag (emoji), curricula[ { id, label, boards[ { id, label, suggestedSubjects[] } ] } ],
 *     defaultCurriculumId
 *   }
 *
 * `suggestedSubjects` is a hint list — the parent can always type their own
 * subjects in the free-text field; we just pre-fill chips here so most
 * parents land on the right combo with one tap.
 */

// Common subject buckets reused across boards
const SCIENCE_CORE = ["Mathematics", "Physics", "Chemistry", "Biology"];
const HUMANITIES_CORE = ["English Language", "English Literature", "History", "Geography", "Economics"];
const BUSINESS_CORE = ["Economics", "Business Studies", "Accounting"];
const STEM_PLUS = [...SCIENCE_CORE, "Computer Science", "Further Mathematics"];

// ---------- per-country maps ----------

export const FOREIGN_CURRICULUM_MAP = {
    GB: {
        name: "United Kingdom",
        flag: "🇬🇧",
        defaultCurriculumId: "uk-national",
        curricula: [
            {
                id: "uk-national",
                label: "UK National Curriculum",
                boards: [
                    { id: "aqa", label: "AQA", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE] },
                    { id: "edexcel", label: "Edexcel (Pearson)", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE, ...BUSINESS_CORE] },
                    { id: "ocr", label: "OCR", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE] },
                    { id: "wjec", label: "WJEC / Eduqas", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE] },
                    { id: "gcse", label: "GCSE", suggestedSubjects: [...SCIENCE_CORE, ...HUMANITIES_CORE, "Computer Science"] },
                    { id: "a-level", label: "A Level", suggestedSubjects: [...STEM_PLUS, ...BUSINESS_CORE, "Psychology"] },
                ],
            },
            {
                id: "cambridge-intl",
                label: "Cambridge International",
                boards: [
                    { id: "igcse", label: "IGCSE", suggestedSubjects: [...SCIENCE_CORE, ...HUMANITIES_CORE, "Computer Science", "Global Perspectives"] },
                    { id: "ial", label: "International A Level (IAL)", suggestedSubjects: [...STEM_PLUS, ...BUSINESS_CORE, "Psychology"] },
                    { id: "ipq", label: "Cambridge Pre-U / Checkpoint", suggestedSubjects: SCIENCE_CORE },
                ],
            },
        ],
    },

    US: {
        name: "United States",
        flag: "🇺🇸",
        defaultCurriculumId: "us-curriculum",
        curricula: [
            {
                id: "us-curriculum",
                label: "US Curriculum",
                boards: [
                    { id: "common-core", label: "Common Core", suggestedSubjects: ["Mathematics", "ELA / English", "Science", "Social Studies"] },
                    { id: "ap", label: "AP (Advanced Placement)", suggestedSubjects: ["AP Calculus", "AP Physics", "AP Chemistry", "AP Biology", "AP Statistics", "AP Economics", "AP English"] },
                    { id: "honors", label: "Honors", suggestedSubjects: ["Honors Math", "Honors English", ...SCIENCE_CORE] },
                    { id: "state", label: "State Curriculum (NY / CA / TX / FL …)", suggestedSubjects: ["Mathematics", "Science", "English", "Social Studies"] },
                ],
            },
            {
                id: "us-ib",
                label: "IB Diploma Programme",
                boards: [
                    { id: "ib-dp", label: "IB DP", suggestedSubjects: ["IB Math AA / AI", "IB Physics", "IB Chemistry", "IB Biology", "IB English", "IB Economics", "TOK"] },
                    { id: "ib-myp", label: "IB MYP", suggestedSubjects: SCIENCE_CORE },
                ],
            },
            {
                id: "us-tests",
                label: "Standardised Tests",
                boards: [
                    { id: "sat", label: "SAT Preparation", suggestedSubjects: ["SAT Reading & Writing", "SAT Math"] },
                    { id: "act", label: "ACT Preparation", suggestedSubjects: ["ACT English", "ACT Math", "ACT Reading", "ACT Science"] },
                ],
            },
        ],
    },

    AU: {
        name: "Australia",
        flag: "🇦🇺",
        defaultCurriculumId: "au-state",
        curricula: [
            {
                id: "au-state",
                label: "Australian Curriculum (State Senior Certificates)",
                boards: [
                    { id: "hsc", label: "HSC (NSW)", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE] },
                    { id: "vce", label: "VCE (Victoria)", suggestedSubjects: [...STEM_PLUS, ...HUMANITIES_CORE, ...BUSINESS_CORE] },
                    { id: "qce", label: "QCE (Queensland)", suggestedSubjects: STEM_PLUS },
                    { id: "wace", label: "WACE (Western Australia)", suggestedSubjects: STEM_PLUS },
                    { id: "sace", label: "SACE (South Australia)", suggestedSubjects: STEM_PLUS },
                    { id: "tasc", label: "TASC (Tasmania)", suggestedSubjects: SCIENCE_CORE },
                    { id: "ntcet", label: "NTCET (Northern Territory)", suggestedSubjects: SCIENCE_CORE },
                ],
            },
            {
                id: "au-intl",
                label: "International Curricula in Australia",
                boards: [
                    { id: "igcse-au", label: "IGCSE", suggestedSubjects: [...SCIENCE_CORE, "Computer Science"] },
                    { id: "ib-au", label: "IB Diploma Programme", suggestedSubjects: ["IB Math", "IB Physics", "IB Chemistry", "IB English", "TOK"] },
                ],
            },
        ],
    },

    CA: {
        name: "Canada",
        flag: "🇨🇦",
        defaultCurriculumId: "ca-provincial",
        curricula: [
            {
                id: "ca-provincial",
                label: "Canadian Provincial Curricula",
                boards: [
                    { id: "ontario", label: "Ontario Curriculum (OSSD)", suggestedSubjects: ["Math (MCV4U/MHF4U)", "Physics SPH4U", "Chemistry SCH4U", "Biology SBI4U", "English ENG4U"] },
                    { id: "alberta", label: "Alberta Curriculum", suggestedSubjects: SCIENCE_CORE },
                    { id: "bc", label: "British Columbia Curriculum", suggestedSubjects: SCIENCE_CORE },
                    { id: "quebec", label: "Quebec Curriculum (CEGEP)", suggestedSubjects: SCIENCE_CORE },
                ],
            },
            {
                id: "ca-intl",
                label: "International in Canada",
                boards: [
                    { id: "ib-ca", label: "IB Diploma Programme", suggestedSubjects: ["IB Math", "IB Physics", "IB English", "TOK"] },
                    { id: "ap-ca", label: "AP (Advanced Placement)", suggestedSubjects: ["AP Calculus", "AP Physics", "AP Chemistry"] },
                ],
            },
        ],
    },

    AE: {
        name: "United Arab Emirates",
        flag: "🇦🇪",
        defaultCurriculumId: "ae-mixed",
        curricula: [
            {
                id: "ae-mixed",
                label: "UAE School Curricula",
                boards: [
                    { id: "cbse", label: "CBSE (Indian curriculum)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English", "Hindi"] },
                    { id: "icse", label: "ICSE / ISC (Indian curriculum)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
                    { id: "british", label: "British Curriculum", suggestedSubjects: ["IGCSE Math", "IGCSE Physics", "IGCSE Chemistry", "IGCSE Biology", "A Level Math"] },
                    { id: "american", label: "American Curriculum", suggestedSubjects: ["AP Calculus", "AP Physics", "AP Chemistry", "AP Biology"] },
                    { id: "ib-ae", label: "IB Curriculum (DP / MYP)", suggestedSubjects: ["IB Math", "IB Physics", "IB English", "TOK"] },
                    { id: "moe", label: "MOE / Emirates School Curriculum", suggestedSubjects: ["Mathematics", "Arabic", "Islamic Studies", "Science", "English"] },
                    { id: "igcse-ae", label: "IGCSE", suggestedSubjects: SCIENCE_CORE },
                    { id: "a-level-ae", label: "A Level", suggestedSubjects: STEM_PLUS },
                ],
            },
        ],
    },

    SG: {
        name: "Singapore",
        flag: "🇸🇬",
        defaultCurriculumId: "sg-mixed",
        curricula: [
            {
                id: "sg-mixed",
                label: "Singapore School System",
                boards: [
                    { id: "sg-primary", label: "Singapore Curriculum (Primary / Secondary)", suggestedSubjects: ["Math", "Science", "English", "Mother Tongue"] },
                    { id: "psle", label: "PSLE", suggestedSubjects: ["English", "Mathematics", "Science", "Mother Tongue"] },
                    { id: "o-level", label: "GCE O-Level", suggestedSubjects: ["E.Math", "A.Math", "Chemistry", "Physics", "Biology", "English"] },
                    { id: "a-level-sg", label: "GCE A-Level (H1 / H2 / H3)", suggestedSubjects: ["H2 Math", "H2 Physics", "H2 Chemistry", "H2 Economics", "GP"] },
                    { id: "ib-sg", label: "IB Diploma", suggestedSubjects: ["IB Math", "IB Physics", "IB English", "TOK"] },
                    { id: "igcse-sg", label: "IGCSE", suggestedSubjects: SCIENCE_CORE },
                ],
            },
        ],
    },

    IN: {
        name: "India",
        flag: "🇮🇳",
        defaultCurriculumId: "in-national",
        curricula: [
            {
                id: "in-national",
                label: "Indian National & State Boards",
                boards: [
                    { id: "cbse-in", label: "CBSE", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
                    { id: "icse-in", label: "ICSE", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
                    { id: "isc-in", label: "ISC", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
                    { id: "state-in", label: "State Boards (MP / Maharashtra / TN / KA …)", suggestedSubjects: ["Mathematics", "Science", "English", "Social Studies"] },
                    { id: "nios", label: "NIOS", suggestedSubjects: ["Mathematics", "Science", "English"] },
                ],
            },
            {
                id: "in-international",
                label: "International in India",
                boards: [
                    { id: "ib-in", label: "IB", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
                    { id: "igcse-in", label: "IGCSE", suggestedSubjects: SCIENCE_CORE },
                ],
            },
        ],
    },
};

// ---------- shared map for remaining global countries ----------

const GULF_PACK = {
    defaultCurriculumId: "gulf-mixed",
    curricula: [{
        id: "gulf-mixed",
        label: "Gulf School Curricula",
        boards: [
            { id: "cbse", label: "CBSE (Indian curriculum)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
            { id: "icse", label: "ICSE / ISC (Indian curriculum)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
            { id: "british", label: "British Curriculum (IGCSE / A Level)", suggestedSubjects: STEM_PLUS },
            { id: "american", label: "American Curriculum (AP / Honors)", suggestedSubjects: ["AP Calculus", "AP Physics", "AP Chemistry"] },
            { id: "ib", label: "IB Curriculum (DP / MYP)", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
            { id: "moe", label: "Local MOE Curriculum", suggestedSubjects: ["Mathematics", "Arabic", "Science", "English"] },
        ],
    }],
};
const EU_PACK = {
    defaultCurriculumId: "eu-mixed",
    curricula: [{
        id: "eu-mixed",
        label: "European School Curricula",
        boards: [
            { id: "national", label: "National Curriculum (Abitur / Bac / Maturità etc.)", suggestedSubjects: ["Mathematik / Maths", "Physics", "Chemistry", "Biology", "English"] },
            { id: "ib-eu", label: "IB Diploma Programme", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
            { id: "igcse-eu", label: "IGCSE / A Level", suggestedSubjects: STEM_PLUS },
            { id: "european-school", label: "European School / Eurobac", suggestedSubjects: ["Maths", "Science", "Mother Tongue", "L2"] },
        ],
    }],
};
const EAST_ASIA_PACK = {
    defaultCurriculumId: "ea-mixed",
    curricula: [{
        id: "ea-mixed",
        label: "International School Curricula",
        boards: [
            { id: "cbse", label: "CBSE", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "English"] },
            { id: "icse", label: "ICSE / ISC", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "English"] },
            { id: "british", label: "British Curriculum (IGCSE / A Level)", suggestedSubjects: STEM_PLUS },
            { id: "american", label: "American Curriculum (AP / Honors)", suggestedSubjects: ["AP Calculus", "AP Physics", "AP English"] },
            { id: "ib", label: "IB Curriculum", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
            { id: "national", label: "Local National Curriculum", suggestedSubjects: ["Mathematics", "Science", "English"] },
        ],
    }],
};
const SEA_PACK = EAST_ASIA_PACK;
const AFRICA_PACK = {
    defaultCurriculumId: "africa-mixed",
    curricula: [{
        id: "africa-mixed",
        label: "African School Curricula",
        boards: [
            { id: "cambridge", label: "Cambridge IGCSE / A Level", suggestedSubjects: STEM_PLUS },
            { id: "ib", label: "IB Diploma", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
            { id: "national", label: "Local National Curriculum (NSC / WAEC / CBC etc.)", suggestedSubjects: ["Mathematics", "Science", "English"] },
            { id: "cbse", label: "CBSE (Indian curriculum)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "English"] },
        ],
    }],
};

[
    ["SA", "Saudi Arabia", "🇸🇦", GULF_PACK],
    ["QA", "Qatar",         "🇶🇦", GULF_PACK],
    ["OM", "Oman",          "🇴🇲", GULF_PACK],
    ["KW", "Kuwait",        "🇰🇼", GULF_PACK],
    ["BH", "Bahrain",       "🇧🇭", GULF_PACK],

    ["DE", "Germany",       "🇩🇪", EU_PACK],
    ["FR", "France",        "🇫🇷", EU_PACK],
    ["IT", "Italy",         "🇮🇹", EU_PACK],
    ["ES", "Spain",         "🇪🇸", EU_PACK],
    ["NL", "Netherlands",   "🇳🇱", EU_PACK],
    ["CH", "Switzerland",   "🇨🇭", EU_PACK],
    ["IE", "Ireland",       "🇮🇪", EU_PACK],
    ["SE", "Sweden",        "🇸🇪", EU_PACK],
    ["NO", "Norway",        "🇳🇴", EU_PACK],
    ["DK", "Denmark",       "🇩🇰", EU_PACK],

    ["NZ", "New Zealand",   "🇳🇿", { defaultCurriculumId: "nz-national", curricula: [{
        id: "nz-national", label: "New Zealand Curriculum",
        boards: [
            { id: "ncea", label: "NCEA (Levels 1, 2 & 3)", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
            { id: "cambridge-nz", label: "Cambridge International (IGCSE / A Level)", suggestedSubjects: STEM_PLUS },
            { id: "ib-nz", label: "IB Diploma", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
        ],
    }] }],

    ["MY", "Malaysia",      "🇲🇾", SEA_PACK],
    ["TH", "Thailand",      "🇹🇭", SEA_PACK],
    ["JP", "Japan",         "🇯🇵", EAST_ASIA_PACK],
    ["KR", "South Korea",   "🇰🇷", EAST_ASIA_PACK],
    ["CN", "China",         "🇨🇳", EAST_ASIA_PACK],
    ["HK", "Hong Kong",     "🇭🇰", { defaultCurriculumId: "hk-mixed", curricula: [{
        id: "hk-mixed", label: "Hong Kong School Curricula",
        boards: [
            { id: "dse", label: "HKDSE", suggestedSubjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
            { id: "british-hk", label: "British Curriculum (IGCSE / A Level)", suggestedSubjects: STEM_PLUS },
            { id: "ib-hk", label: "IB Diploma", suggestedSubjects: ["IB Math", "IB Physics", "IB English"] },
            { id: "ap-hk", label: "American Curriculum (AP / Honors)", suggestedSubjects: ["AP Calculus", "AP Physics", "AP Chemistry"] },
        ],
    }] }],

    ["ZA", "South Africa",  "🇿🇦", AFRICA_PACK],
    ["KE", "Kenya",         "🇰🇪", AFRICA_PACK],
    ["NG", "Nigeria",       "🇳🇬", AFRICA_PACK],
].forEach(([code, name, flag, pack]) => {
    FOREIGN_CURRICULUM_MAP[code] = { name, flag, ...pack };
});

// Helper — list (code, name, flag) tuples sorted for the country selector.
export const FOREIGN_COUNTRY_OPTIONS = Object.entries(FOREIGN_CURRICULUM_MAP)
    .map(([code, v]) => ({ code, name: v.name, flag: v.flag }))
    .sort((a, b) => a.name.localeCompare(b.name));

// Lookup helper — returns null when the country has no mapping (then the
// form falls back to a free-text board input).
export function getCurriculumPack(countryCodeOrName) {
    if (!countryCodeOrName) return null;
    const key = String(countryCodeOrName).toUpperCase();
    if (FOREIGN_CURRICULUM_MAP[key]) return FOREIGN_CURRICULUM_MAP[key];
    // Fallback: match by country display name (case-insensitive)
    const wanted = String(countryCodeOrName).trim().toLowerCase();
    const hit = Object.entries(FOREIGN_CURRICULUM_MAP).find(([, v]) => v.name.toLowerCase() === wanted);
    return hit ? hit[1] : null;
}
